/**
 * referenceRange.js — pick the right ParameterRange for a patient (gender + age).
 *
 * Ranges are stored with a free-text age-band label in `referenceRange`
 * ("Any", "1 month to 1 year", "upto 3 months", "Above 2 years",
 *  "Newborn (0 day to 3 day)", "0 to 2 Days", …) plus a `gender`
 * ("Both" / "Male" / "Female" / "Kids").
 *
 * This mirrors the server-side parser in server/src/utils/ageToKeyFromDob.js —
 * containment matching (does the patient's age fall inside the band?), never
 * exact label equality.
 */

const DAY = 1;
const WEEK = 7;
const MONTH = 30.4375;
const YEAR = 365.25;

const parseDays = (amount, unit) => {
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return null;
  const u = String(unit || "").toLowerCase().trim();
  if (u.startsWith("day")) return n * DAY;
  if (u.startsWith("week")) return n * WEEK;
  if (u.startsWith("month")) return n * MONTH;
  if (u.startsWith("year") || u === "yr" || u === "yrs") return n * YEAR;
  return null;
};

const extractSingle = (phrase) => {
  const m = String(phrase).match(/(\d+(?:\.\d+)?)\s*(days?|weeks?|months?|years?|yr|yrs)/i);
  if (!m) return null;
  return parseDays(m[1], m[2]);
};

/** Parse an age-band label → { minDays, maxDays } or null when unparseable. */
export function parseAgeBand(label) {
  const raw = String(label || "").trim();
  const lo = raw.toLowerCase();

  if (!lo || lo === "any") return { minDays: 0, maxDays: Infinity };
  if (lo === "premature born") return { minDays: 0, maxDays: 0 };
  if (lo === "2 weeks") return { minDays: 14, maxDays: 21 };
  if (["post menopausal", "pregnant female", "non pregnant female"].includes(lo)) return null;

  {
    const m = lo.match(/adolescents?\s*[([]?\s*(\d+)\s*[-–to]+\s*(\d+)\s*yrs?/i);
    if (m) return { minDays: Number(m[1]) * YEAR, maxDays: Number(m[2]) * YEAR };
  }
  {
    const m = lo.match(/above\s+(\d+(?:\.\d+)?)\s*(days?|weeks?|months?|years?|yr|yrs?)/i);
    if (m) { const d = parseDays(m[1], m[2]); if (d !== null) return { minDays: d, maxDays: Infinity }; }
  }
  {
    const m = lo.match(/upto?\s+(\d+(?:\.\d+)?)\s*(days?|weeks?|months?|years?|yr|yrs?)/i);
    if (m) { const d = parseDays(m[1], m[2]); if (d !== null) return { minDays: 0, maxDays: d }; }
  }
  {
    const m = lo.match(/(\d+(?:\.\d+)?)\s*(days?|weeks?|months?|years?)\s+and\s+above/i);
    if (m) { const d = parseDays(m[1], m[2]); if (d !== null) return { minDays: d, maxDays: Infinity }; }
  }

  if (!/[-–]|to\s|\bto\b/.test(lo)) {
    const single = extractSingle(lo);
    if (single !== null) return { minDays: single * 0.9, maxDays: single * 1.1 };
  }

  const SEP = /\s+to\s+|\s*[-–]\s*/i;
  const parts = lo.split(SEP);
  if (parts.length >= 2) {
    const left = parts[0].trim();
    const right = parts[parts.length - 1].trim();
    const rightHasUnit = /(days?|weeks?|months?|years?|yr|yrs?)/i.test(right);
    const leftUnit = left.match(/(days?|weeks?|months?|years?|yr|yrs?)/i)?.[0] || "";

    let minDays = extractSingle(left);
    let maxDays = rightHasUnit
      ? extractSingle(right)
      : parseDays(right.match(/(\d+(?:\.\d+)?)/)?.[1], leftUnit);

    // Borrow the other side's unit for bare numbers ("0 to 30 Days" → 0 days)
    const rightUnit = right.match(/(days?|weeks?|months?|years?|yr|yrs?)/i)?.[0] || "";
    if (minDays === null && rightUnit) {
      const ln = left.match(/^(\d+(?:\.\d+)?)$/)?.[1];
      if (ln !== undefined) minDays = parseDays(ln, rightUnit);
    }

    if (minDays !== null && maxDays !== null) return { minDays, maxDays };

    const leftNum = left.match(/^(\d+(?:\.\d+)?)/)?.[1];
    const rightNum = right.match(/^(\d+(?:\.\d+)?)/)?.[1];
    if (leftNum && rightNum && leftUnit) {
      minDays = parseDays(leftNum, leftUnit);
      maxDays = parseDays(rightNum, leftUnit);
      if (minDays !== null && maxDays !== null) return { minDays, maxDays };
    }
  }
  return null;
}

const normGender = (g) => {
  const x = String(g || "").trim().toLowerCase();
  if (x === "m" || x === "male") return "male";
  if (x === "f" || x === "female") return "female";
  if (x === "k" || x === "kid" || x === "kids") return "kids";
  return "";
};
const rangeGenderTier = (rangeGender, patientGender) => {
  const rg = normGender(rangeGender);
  if (!rg || rg === "both" || String(rangeGender || "").trim().toLowerCase() === "both") return 1; // generic
  return rg === patientGender ? 0 : -1; // 0 = exact match, -1 = other gender (excluded)
};

export function patientAgeInDays(patient) {
  const dob = patient?.dob;
  if (dob) {
    const d = new Date(dob);
    if (!Number.isNaN(d.getTime()) && d <= new Date()) return (Date.now() - d.getTime()) / 86400000;
  }
  const n = Number(patient?.age);
  if (Number.isFinite(n) && n >= 0) return n * YEAR + YEAR / 2; // mid-year to avoid band boundaries
  return null;
}

const isAnyBand = (v) => { const s = String(v || "").trim().toLowerCase(); return !s || s === "any"; };

/**
 * Pick the best range for a patient:
 *   gender-exact + age-band match  >  gender-Both + age-band match
 *   >  gender-exact + "Any" band   >  gender-Both + "Any" band  >  first range.
 * Within a tier the narrowest age band wins.
 */
export function pickBestRange(ranges, patient) {
  const list = Array.isArray(ranges) ? ranges.filter(Boolean) : [];
  if (list.length <= 1) return list[0] || null;

  const pg = normGender(patient?.gender);
  const days = patientAgeInDays(patient);

  const scored = list
    .map((r) => {
      const gTier = rangeGenderTier(r.gender, pg);
      if (gTier < 0) return null; // other gender never applies
      const band = isAnyBand(r.referenceRange) ? null : parseAgeBand(r.referenceRange);
      const ageMatch = band && days !== null && days >= band.minDays && days <= band.maxDays;
      const anyBand = isAnyBand(r.referenceRange) || (!band && true);
      if (!ageMatch && !anyBand) return null; // a specific band that doesn't contain the age
      const tier = (ageMatch ? 0 : 2) + gTier; // 0..3
      const width = band ? band.maxDays - band.minDays : Infinity;
      return { r, tier, width };
    })
    .filter(Boolean)
    .sort((a, b) => a.tier - b.tier || a.width - b.width);

  return scored[0]?.r || list[0];
}

/** "32 yrs" / "7 months" / "12 days" — from dob when available, else the stored age. */
export function formatPatientAge(patient) {
  const days = patientAgeInDays(patient);
  if (days === null) return "";
  if (patient?.dob) {
    if (days < 60) return `${Math.max(0, Math.round(days))} days`;
    if (days < 2 * YEAR) return `${Math.round(days / MONTH)} months`;
    return `${Math.floor(days / YEAR)} yrs`;
  }
  return `${Number(patient.age)} yrs`;
}
